/**
 * relationalContextEngine.js
 *
 * Infers the RELATIONAL STRUCTURE of the current pastoral conversation.
 * Answers: "What kind of human relationship is at the center of this?"
 *
 * NOT a graph database. NOT entity linking. Just pastoral inference
 * from text + intent + entity context to understand the relational
 * frame the pastor is operating within.
 *
 * Structures:
 *   matrimonio           — marital relationship at center
 *   familia              — family unit (parents/children, household)
 *   conflicto_familiar   — family tension or break
 *   liderazgo            — church leadership/elder context
 *   equipo_ministerial   — ministry team or deacon group
 *   discipulado          — discipleship relationship
 *   visita_pastoral      — individual pastoral visit
 *   congregacion         — whole congregation or broad church context
 *   conflicto_relacional — interpersonal conflict not family
 *   cuidado_personal     — individual care (one person, one-on-one)
 *   general              — no clear relational structure inferred
 */

const STRUCTURE_PATTERNS = [
  {
    structure: "matrimonio",
    patterns: [
      /esposo|esposa|matrimonio|conyugal|pareja\s+(?:de|en)|casad[ao]|bodas|reconciliaci[oó]n\s+(?:matrimonial|conyugal)/i,
    ],
  },
  {
    structure: "conflicto_familiar",
    patterns: [
      /conflicto\s+(?:familiar|en\s+(?:la\s+)?familia)|pelea\s+(?:familiar|en\s+casa)|tension\s+(?:familiar|en\s+la\s+familia)|divisi[oó]n\s+familiar|separaci[oó]n\s+familiar/i,
    ],
  },
  {
    structure: "familia",
    patterns: [
      /familia\s+(?:de|en)|familia(?!\s+(?:Rivera|García|López|Martínez|Hernández|González|Rodríguez|Pérez|Sánchez|Flores|Torres|Ramírez|Cruz|Morales|Ortega|Castillo|Ramos|Gutiérrez|Mendoza|Jiménez))\b|hijo[s]?\b|hija[s]?\b|padres\b|madre\s+(?:de|sola)|padre\s+(?:de|solo)|hermano[s]?\s+(?:de|en)|familia\s+pastoral/i,
    ],
  },
  {
    structure: "liderazgo",
    patterns: [
      /anciano[s]?|di[aá]cono[s]?|junta\s+(?:directiva|de\s+iglesia)|liderazgo|l[ií]der(?:es)?\s+(?:de|en\s+la)|pastor(?:es)?\s+(?:asociado|auxiliar|local)|directiva|consejo\s+(?:de\s+iglesia|pastoral)/i,
    ],
  },
  {
    structure: "equipo_ministerial",
    patterns: [
      /equipo\s+(?:de\s+ministerio|ministerial|de\s+trabajo)|voluntarios|colaboradores|ministerio\s+(?:de\s+jóvenes|infantil|de\s+mujeres|de\s+música|de\s+diaconisas)/i,
    ],
  },
  {
    structure: "discipulado",
    patterns: [
      /discipulad[ao]|discipur|mentor|mentoreo|acompañamiento\s+(?:espiritual|personal)|guía\s+espiritual|catecúmeno|proceso\s+(?:de\s+bautismo|de\s+decisión)|instrucci[oó]n\s+(?:bíblica|espiritual)/i,
    ],
  },
  {
    structure: "congregacion",
    patterns: [
      /congregaci[oó]n|iglesia\s+(?:en\s+general|local|como\s+cuerpo)|toda\s+la\s+iglesia|la\s+comunidad\s+(?:de\s+fe|eclesial)|el\s+cuerpo\s+(?:de\s+Cristo|de\s+la\s+iglesia)|miembros\s+en\s+general/i,
    ],
  },
  {
    structure: "conflicto_relacional",
    patterns: [
      /conflicto\s+con|tensión\s+con|discusión\s+con|problema\s+con|relación\s+difícil|resentimiento|herida\s+(?:relacional|entre)/i,
    ],
  },
  {
    structure: "visita_pastoral",
    patterns: [
      /visit(?:ar?|a)\s+(?:a|esta)|visita\s+(?:pastoral|domiciliaria|a\s+(?:la\s+)?(?:familia|persona))/i,
    ],
  },
];

/**
 * @param {{
 *   text:          string,
 *   intent:        string,
 *   entityContext: object|null,
 * }} params
 * @returns {{
 *   structure:  string,
 *   confidence: "low"|"medium"|"high",
 * }}
 */
export function inferRelationalContext({ text, intent, entityContext }) {
  const t = text ?? "";

  // Match text patterns
  const matched = [];
  for (const { structure, patterns } of STRUCTURE_PATTERNS) {
    if (patterns.some((p) => p.test(t))) {
      matched.push(structure);
    }
  }

  // Intent-based hints
  if (intent === "family_followup" && matched.length === 0) {
    matched.push("familia");
  }
  if (intent === "visit_plan" && matched.length === 0) {
    matched.push("visita_pastoral");
  }

  // Entity type hints
  if (entityContext?.type === "family" && matched.length === 0) {
    matched.push("familia");
  }

  if (matched.length === 0) {
    return { structure: "general", confidence: "low" };
  }

  // Priority: more specific wins
  const PRIORITY = [
    "matrimonio", "conflicto_familiar", "liderazgo", "conflicto_relacional",
    "equipo_ministerial", "discipulado", "familia", "congregacion", "visita_pastoral",
  ];
  const primary    = PRIORITY.find((s) => matched.includes(s)) ?? matched[0];
  const confidence = matched.length >= 2 ? "high" : "medium";

  return { structure: primary, confidence };
}
